/**
 * Where a clicked Media Metrics tile goes, and how the filter travels with it.
 *
 * A tile is a count of media. Clicking it opens the job's own page on the processing tab, with the tape
 * table narrowed to exactly the media the tile counted — so the number stops being an assertion and
 * becomes a list somebody can read, sort and export.
 *
 * The filter is carried in the query string rather than in router state or a context, for three reasons
 * that all matter to whoever is looking at the table:
 *   - It survives a reload, so the filtered view is a page rather than a moment.
 *   - It is copyable: "have a look at these" is a URL, which is how one operator hands a problem to
 *     another.
 *   - The destination page can be arrived at cold — from a link in an email — and still know what it is
 *     meant to be showing, with no dependency on having come from the dashboard.
 */
import {fetchJobs} from "../../../utils/apiCalls";

/** Query parameter naming the {@link MEDIA_FILTERS} filter the tape table should apply. */
export const MEDIA_FILTER_PARAM = 'media';

/** Query parameter naming the tab to open. Only 'processing' is acted on today. */
export const TAB_PARAM = 'tab';

/** The value {@link TAB_PARAM} takes to open the processing tab, where the tape table lives. */
export const PROCESSING_TAB = 'processing';

/**
 * The query string that opens a job page on its processing tab with the tape table filtered.
 *
 * Both parameters, always: the tab is not implied by the filter. Job Management picks its tab from the
 * order's status, so an order still marked as shipping would open on the shipping tab with a filter
 * applied to a table nobody can see.
 *
 * @param {string} filterKey - A {@link MEDIA_FILTERS} key.
 */
export const mediaFilterSearch = (filterKey) =>
    `?${TAB_PARAM}=${PROCESSING_TAB}&${MEDIA_FILTER_PARAM}=${encodeURIComponent(filterKey)}`;

/** The route of the Data Management page, whose Restore tab holds the account-wide tape list. */
export const DATA_MANAGEMENT_PATH = '/DataManagement';

/**
 * The query string that opens the Data Management tape list filtered.
 *
 * No tab parameter, unlike the job page: Data Management opens on Restore showing tapes, which is the list
 * the filter applies to, so there is nothing to ask it to switch to.
 *
 * Why this destination for a tile whose media is not one job's: the page is scoped by the same header Job
 * selector the dashboard is, so whatever the reader had selected is still selected when they arrive —
 * every job of the account if that is what they were looking at. The job page cannot do that; it is one
 * order, reached by a uuid that has to be looked up first, and it can only ever show a single job's media.
 *
 * @param {string} filterKey - A {@link MEDIA_FILTERS} key.
 */
export const dataManagementFilterSearch = (filterKey) =>
    `?${MEDIA_FILTER_PARAM}=${encodeURIComponent(filterKey)}`;

/**
 * The path of a job's own page, which is a different route depending on who is looking.
 *
 * Staff manage orders at /JobManagement/<uuid>; a customer tracks them at /JobTracking/<uuid>. The two
 * routes are mutually exclusive in App.js — each renders NotFound for the audience the other serves — so
 * choosing by the same `order` feature App.js gates on is what keeps a clicked tile from landing a
 * customer on a Not Found page. Both routes render the tape table, so both can honour the filter.
 *
 * @param {string} orderUuid - The Nexus order uuid; the ArkBridge job id will not resolve these routes.
 * @param {boolean} canManageOrders - Whether the viewer has the `order` feature.
 */
export const jobPagePath = (orderUuid, canManageOrders) =>
    `/${canManageOrders ? 'JobManagement' : 'JobTracking'}/${orderUuid}`;

/**
 * Finds the Nexus order uuid for an ArkBridge job id.
 *
 * Needed because the two systems identify a job differently and the dashboard only holds the ArkBridge
 * side of it: the job selector is built from `linked_orders`, which the customers endpoint serialises as
 * `{order_number, tapeark_job_id}` and nothing else, while the routes are keyed by the Nexus order uuid.
 *
 * Resolved through the orders search, which already indexes `tapeark_job_id` — so this needs no change
 * to nexus-api. The search is a `contains` match across three fields, so it can return an order whose
 * order_number merely contains the digits; the exact comparison on `tapeark_job_id` afterwards is what
 * makes the answer right rather than plausible.
 *
 * Returns null rather than throwing when there is no such order, which is a real state: ArkBridge holds
 * jobs that were never onboarded as Nexus orders, and those have no page to navigate to.
 *
 * @param {number|string} jobId - The ArkBridge job id.
 * @returns {Promise<string|null>} The order uuid, or null if no order claims that job id.
 */
export async function findOrderUuidByJobId(jobId) {
    const data = await fetchJobs({search: String(jobId)});
    const orders = Array.isArray(data?.results) ? data.results : [];
    const match = orders.find(order => String(order?.tapeark_job_id) === String(jobId));
    return match?.uuid ?? null;
}
